import React, { useEffect, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Switch, useLocation, useHistory } from 'react-router-dom';
import i18n from 'I18n';

import DetailRedeem from 'component/campaign/DetailRedeem';
import AboutNavigator from 'component/common/AboutNavigator';
import AuthRequiredView from 'component/common/AuthRequiredView';
import CookieBar from 'component/common/CookieBar';
import { CustomToast } from 'component/common/CustomToast';
import Footer from 'component/common/Footer';
import OpenAppBanner from 'component/common/OpenAppBanner';
import ScrollToTop from 'component/common/ScrollToTop';
import UserNavigator from 'component/common/UserNavigator';
import Navigator from 'component/common/navigator/Navigator';
import CouponDetail from 'component/coupon/CouponDetail';
import CouponSelectStore from 'component/coupon/CouponSelectStore';
import UseCoupon from 'component/coupon/UseCoupon';
import BrowserNotSupport from 'component/error/BrowserNotSupport';
import NotFoundPage from 'component/error/NotFound';
import Login from 'component/login/Login';
import ActivityLogMobile from 'component/profile/ActivityLogMobile';
import LevelBenefitMobile from 'component/profile/LevelBenefitMobile';
import LevelRuleMobile from 'component/profile/LevelRuleMobile';
import RewardStatusMobile from 'component/profile/RewardStatusMobile';
import TransactionHistoryListMobile from 'component/profile/TransactionHistoryListMobile';
import UseCashDollar from 'component/profile/UseCashDollar';
import CampaignDetail from 'container/CampaignDetail';
import CreateRewardClaim from 'container/CreateRewardClaim';
import EventRegisterResult from 'container/EventRegisterResult';
import Home from 'container/Home';
import Inbox from 'container/Inbox';
import InboxDetailMobile from 'container/InboxDetailMobile';
import Offer from 'container/Offer';
import ReachLimitDetailMobile from 'container/ReachLimitDetailMobile';
import RedeemGiftCard from 'container/RedeemGiftCard';
import StoreDetail from 'container/StoreDetail';
import TransactionDetailMobile from 'container/TransactionDetailMobile';
import TransactionHistoryDetailMobile from 'container/TransactionHistoryDetailMobile';
import TransactionList from 'container/TransactionList';
import UnsubscribeEmail from 'container/UnsubscribeEmail';
import NoWebappMemberAccountCompletePage from 'component/common/NoWebappHappyDownloadPage'
import MaintenanceOverlay from 'container/MaintenanceOverlay'

import { createAction } from 'utils';
import { SHOW_NAVIGATOR } from 'utils/Config';
import { onlyShowWhiteListPage, NO_WEBAPP_WHITE_PAGE_LIST } from 'utils/UIConfigUtils'
import { inMiniProgram } from 'utils/WeChatUtil';

import './App.scss';

const PageRouteList = [
  {
    path: '/home',
    component: Home,
    isAuthProtected: false,
  },
  {
    path: '/about',
    component: AboutNavigator,
    isAuthProtected: false,
  },
  {
    path: '/activity_log/:id/reach_limit',
    component: ReachLimitDetailMobile,
    isAuthProtected: true,
  },
  {
    path: '/activity_log',
    component: ActivityLogMobile,
    isAuthProtected: true,
  },
  {
    path: '/browser-support',
    component: BrowserNotSupport,
    isAuthProtected: false,
  },
  {
    path: '/campaign/detail',
    component: DetailRedeem,
    isAuthProtected: false,
  },
  {
    path: '/campaign/:id/register_success',
    component: EventRegisterResult,
    isAuthProtected: false,
  },
  {
    path: '/user/completed_success',
    component: NoWebappMemberAccountCompletePage,
    isAuthProtected: false,
  },
  {
    path: '/campaign/:id',
    component: CampaignDetail,
    isAuthProtected: false,
  },
  {
    path: '/coupon/:id/select_store',
    component: CouponSelectStore,
    isAuthProtected: true,
  },
  {
    path: '/coupon/:id',
    component: CouponDetail,
    isAuthProtected: true,
  },
  {
    path: '/ticket/:id',
    component: CouponDetail,
    isAuthProtected: true,
  },
  {
    path: '/inbox',
    component: Inbox,
    isAuthProtected: true,
  },
  {
    path: '/inboxDetail/:id',
    component: InboxDetailMobile,
    isAuthProtected: true,
  },
  {
    path: '/level_benefit',
    component: LevelBenefitMobile,
    isAuthProtected: true,
  },
  {
    path: '/level_rule',
    component: LevelRuleMobile,
    isAuthProtected: true,
  },
  {
    path: '/login_callback',
    component: Login,
    isAuthProtected: false,
  },
  {
    path: '/offer/:page',
    component: Offer,
    isAuthProtected: false,
  },
  {
    path: '/reward_status',
    component: RewardStatusMobile,
    isAuthProtected: true,
  },
  {
    path: '/storeDetail/:id',
    component: StoreDetail,
    isAuthProtected: false,
  },
  // {
  //   path: '/transaction/create',
  //   component: CreateRewardClaim,
  //   isAuthProtected: true,
  // },
  {
    path: '/transaction',
    component: TransactionList,
    isAuthProtected: true,
  },
  {
    path: '/transactionDetail/:id',
    component: TransactionDetailMobile,
    isAuthProtected: true,
  },
  {
    path: '/transaction_history',
    component: TransactionHistoryListMobile,
    isAuthProtected: true,
  },
  {
    path: '/transaction_history_detail/:id',
    component: TransactionHistoryDetailMobile,
    isAuthProtected: true,
  },
  {
    path: '/use_cash_dollar',
    component: UseCashDollar,
    isAuthProtected: true,
  },
  {
    path: '/use_coupon',
    component: UseCoupon,
    isAuthProtected: true,
  },
  {
    path: '/user/:page',
    component: UserNavigator,
    isAuthProtected: true,
  },
  {
    path: '/unsubscribe_email',
    component: UnsubscribeEmail,
    isAuthProtected: false,
  },
  {
    path: '/',
    component: Login,
    isAuthProtected: false,
    exact: true,
  },
];

function AppContent() {
  const { language } = useSelector((state) => ({
    language: state.users.language,
  }));
  const dispatch = useDispatch();
  const history = useHistory()
  const location = useLocation()
  useEffect(() => {
    document.documentElement.lang = language;
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (!SHOW_NAVIGATOR) {
      document
        .getElementById('root')
        .style.setProperty('--page-nav-height', '0px');
    }
    dispatch({ type: "enforceApp/getMaintenanceInfo" })
  }, []);

  useEffect(() => {
    if (window.self !== window.top) {
      dispatch(
        createAction('users/updateState')({
          isOpenInOtherSite: true,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isInMiniProgram = inMiniProgram()
    if (isInMiniProgram && !location?.pathname?.includes("404_not_found_page")) {
      dispatch({ type: "weChat/setUpWeChatConfig", payload: { onFailed: () => { history.push('/404_not_found_page') } } })
      const element = document.querySelector('.footer_container');
      if (element) {
        element.classList.add('we-chat-safe-area');
      }
      dispatch({ type: "users/updateState", payload: { showOpenAppBanner: false } })
    }
    dispatch({ type: "enforceApp/getMaintenanceInfo" })
  }, [location])

  const finalRoutes = useMemo(()=> {
    const showWhiteListPage = onlyShowWhiteListPage()
    if (showWhiteListPage) {
      return PageRouteList.filter((route) => NO_WEBAPP_WHITE_PAGE_LIST.includes(route.path))
    }
    return PageRouteList
  }, [])

  const hideNavigator =
    window.location.href?.indexOf('/redeem-gift-card') !== -1;

  const isTransactionHistoryDetailPage =
    window.location.href?.indexOf('/transaction_history_detail') !== -1;
  const g6Background = isTransactionHistoryDetailPage;

  const noNavigatorPage = () => {
    return (
      <Switch>
        <Route path="/redeem-gift-card" component={RedeemGiftCard} />
      </Switch>
    );
  };

  return (
    <>
      <CustomToast />
      <ScrollToTop>
        <div className="app_container">
          {hideNavigator ? (
            noNavigatorPage()
          ) : (
            <>
              <OpenAppBanner />
              <div
                className={`app_navigator_container ${
                  g6Background ? 'g6_background' : ''
                }
                ${SHOW_NAVIGATOR ? '' : 'hidden'}
                `}
              >
                <Navigator />
              </div>
              <Container className="page_container">
                {/* A <Switch> looks through its children <Route>s and
              renders the first one that matches the current URL. */}
                <Switch>
                  {finalRoutes?.map((item) => (
                    <Route
                      path={item?.path}
                      exact={item?.exact}
                      {...(item?.isAuthProtected
                        ? {
                            render: (props) => (
                              <AuthRequiredView>
                                <item.component {...props} />
                              </AuthRequiredView>
                            ),
                          }
                        : { component: item.component })}
                    />
                  ))}
                  <Route component={NotFoundPage} />
                </Switch>
              </Container>
              <CookieBar />
              <Footer />
            </>
          )}
        </div>
      </ScrollToTop>
      <MaintenanceOverlay />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent/>
    </Router>
  )
}

export default App;
